import { type ChangeEvent, type FC, type FormEvent, useEffect, useState } from 'react'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa6'

export type DuaBottomPaginationProps = {
  /** The current active page number */
  page: number
  /** The total number of pages */
  maxPage: number
  /** The minimum page boundary (default: 1) */
  minPage?: number
  /** Callback fired when user clicks previous page */
  onPreviousPage: () => void
  /** Callback fired when user clicks next page */
  onNextPage: () => void
  /** Callback fired when user submits a new page number */
  onGoToPage: (_targetPage: number) => void
  /** Whether currently on the first page */
  isFirstPage: boolean
  /** Whether currently on the last page */
  isLastPage: boolean
}

/**
 * Bottom pagination controls offering previous/next page buttons and a direct page number input.
 */
const DuaBottomPagination: FC<DuaBottomPaginationProps> = ({
  page,
  maxPage,
  minPage = 1,
  onPreviousPage,
  onNextPage,
  onGoToPage,
  isFirstPage,
  isLastPage,
}) => {
  const [pageInput, setPageInput] = useState(String(page))

  useEffect(() => {
    setPageInput(String(page))
  }, [page])

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPageInput(event.target.value)
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    const targetPage = Number(pageInput)
    if (!isNaN(targetPage) && targetPage >= minPage && targetPage <= maxPage) {
      onGoToPage(targetPage)
    } else {
      setPageInput(String(page))
    }
  }

  return (
    <nav
      aria-label="Sayfa gezintisi"
      className="flex items-center justify-center gap-2 sm:gap-3 mt-4 mb-2"
    >
      {/* Previous Page Button */}
      <button
        type="button"
        onClick={onPreviousPage}
        disabled={isFirstPage}
        aria-label="Önceki Sayfa"
        className="flex items-center gap-1.5 px-4 sm:px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-200 bg-white dark:bg-[#13222e] hover:bg-slate-100 dark:hover:bg-[#1a2d3c] text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-[#1e3445]/80 shadow-xs disabled:opacity-30 disabled:pointer-events-none active:scale-95"
      >
        <FaChevronLeft className="text-xs" />
        <span>Önceki Sayfa</span>
      </button>

      {/* Direct Page Input Form */}
      <form onSubmit={handleSubmit} className="flex items-center">
        <input
          type="number"
          min={minPage}
          max={maxPage}
          value={pageInput}
          onChange={handleInputChange}
          aria-label="Sayfa numarası"
          title="Sayfaya gitmek için yazıp Enter'a basın"
          className="w-12 sm:w-14 h-9 rounded-full bg-slate-100 dark:bg-[#0f1d28] border border-slate-300 dark:border-[#1e3445] text-center font-bold text-xs sm:text-sm text-slate-800 dark:text-white outline-hidden focus:ring-2 focus:ring-teal-500/50 transition-all shadow-inner"
        />
      </form>

      {/* Next Page Button */}
      <button
        type="button"
        onClick={onNextPage}
        disabled={isLastPage}
        aria-label="Sonraki Sayfa"
        className="flex items-center gap-1.5 px-4 sm:px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-200 bg-teal-600 hover:bg-teal-500 text-white shadow-xs disabled:opacity-30 disabled:pointer-events-none active:scale-95"
      >
        <span>Sonraki Sayfa</span>
        <FaChevronRight className="text-xs" />
      </button>
    </nav>
  )
}

export default DuaBottomPagination
